import React, { useCallback, useEffect, useState } from "react";
import { FaShieldAlt } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { isPlatformAdmin } from "../../constants/roles";
// Reuse the API Keys settings visual language (sections, master-toggle, switch).
import "./ApiKeysSettings.css";

/**
 * Advanced Analysis settings — behavior toggles for the sandbox detonation
 * ("Perform Advanced Analysis") feature. Currently a single control: whether to
 * hide the entry point once both the AI and Detection engines confirm Malicious.
 * Backed by /api/detonations/settings (GET/PUT, Platform Admin only).
 */
export default function AdvancedAnalysisSettings() {
  const { token, user } = useAuth();
  const isAdmin = isPlatformAdmin(user?.role);

  const [suppressOnMalicious, setSuppressOnMalicious] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState("");
  const [error, setError]     = useState("");

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/detonations/settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setSuppressOnMalicious(json.suppressAdvancedWhenMalicious !== false);
    } catch (err) {
      setError(err.message || "Failed to load Advanced Analysis settings");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleSuppressOnMalicious = useCallback(async () => {
    const next = !suppressOnMalicious;
    setSaving(true);
    setMsg("");
    // Optimistic flip so the switch feels instant; revert on failure.
    setSuppressOnMalicious(next);
    try {
      const res = await fetch("/api/detonations/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ suppressAdvancedWhenMalicious: next }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSuppressOnMalicious(json.suppressAdvancedWhenMalicious !== false);
      setMsg(json.suppressAdvancedWhenMalicious !== false
        ? "Advanced Analysis will be hidden when both engines confirm Malicious."
        : "Advanced Analysis will always be offered.");
      setTimeout(() => setMsg(""), 3500);
    } catch (err) {
      setSuppressOnMalicious(!next); // revert
      setMsg(`Error: ${err.message || "Failed to update setting"}`);
    } finally {
      setSaving(false);
    }
  }, [suppressOnMalicious, token]);

  return (
    <div className="ik-page">
      {loading && <div className="ik-state-msg">Loading…</div>}
      {error   && <div className="ik-state-err">{error}</div>}

      <section className="ik-section">
        <div className="ik-section-hd">
          <h2 className="ik-section-title">Advanced Analysis</h2>
          <p className="ik-section-desc">
            Behavior of the sandbox detonation feature ("Perform Advanced Analysis") shown during
            email investigations.
          </p>
        </div>

        <div className={`ik-ai-master${suppressOnMalicious ? "" : " ik-ai-master-off"}`}>
          <div className="ik-ai-master-icon">
            <FaShieldAlt />
          </div>
          <div className="ik-ai-master-info">
            <div className="ik-ai-master-title">
              Hide when verdict is confirmed
              <span className={`ik-ai-master-pill ${suppressOnMalicious ? "on" : "off"}`}>
                {suppressOnMalicious ? "Enabled" : "Disabled"}
              </span>
            </div>
            <p className="ik-ai-master-desc">
              {suppressOnMalicious
                ? "Advanced Analysis is hidden once the AI and Detection engines both confirm an email as Malicious — a sandbox can only escalate to Malicious, so it adds nothing. Turn off to always offer detonation."
                : "Advanced Analysis is always offered, even when both engines already confirm an email as Malicious."}
            </p>
            {msg && (
              <span className={msg.startsWith("Error") ? "ik-msg-err" : "ik-msg-ok"}>
                {msg}
              </span>
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={suppressOnMalicious}
            aria-label="Hide Advanced Analysis when both engines confirm Malicious"
            className={`ik-ai-switch ${suppressOnMalicious ? "on" : "off"}`}
            onClick={toggleSuppressOnMalicious}
            disabled={saving || loading || !isAdmin}
          >
            <span className="ik-ai-switch-knob" />
          </button>
        </div>
      </section>
    </div>
  );
}
